import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { DateAdapter, MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TransportSegmentsApiService } from '@/bag/transport-segments/transport-segments.api.service';
import { TransportTermList } from '@/bag/bag-transport-term/bag-transport-term.model';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { finalize } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { PolishDateAdapter } from '@/common/polish-date-adapter/polish-date-adapter';
import { MatSortModule, Sort } from '@angular/material/sort';

@Component({
	selector: 'app-transport-segments-add',
	standalone: true,
	imports: [
		NavbarModule,
		DatePipe,
		ReactiveFormsModule,
		MatButtonModule,
		MatInputModule,
		MatFormFieldModule,
		MatCardModule,
		MatTableModule,
		MatDatepickerModule,
		MatNativeDateModule,
		MatCheckboxModule,
		MatProgressSpinnerModule,
		MatSortModule,
	],
	templateUrl: './transport-segments-add.component.html',
	styleUrl: './transport-segments-add.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [{ provide: DateAdapter, useClass: PolishDateAdapter }],
})
export class TransportSegmentsAddComponent implements OnInit {
	readonly nameControl = new FormControl('', { nonNullable: true, validators: [Validators.required] });
	readonly filtersForm = inject(FormBuilder).group({
		search: ['', Validators.required],
		accommodationAfter: [null as Date | null, Validators.required],
		accommodationBefore: [null as Date | null, Validators.required],
	});
	readonly selectedTerms = signal<number[]>([]);
	readonly selectedTermEntities = signal<Record<number, TransportTermList>>({});
	readonly lastSearchResults = signal<TransportTermList[]>([]);
	readonly transportTerms = signal<TransportTermList[]>([]);
	readonly isSaving = signal(false);
	readonly editedSegmentId = signal<number | null>(null);
	readonly displayedColumns: Array<'select' | 'id' | 'name' | 'accommodationLower' | 'accommodationUpper'> = [
		'select',
		'id',
		'name',
		'accommodationLower',
		'accommodationUpper',
	];

	private readonly transportSegmentsApiService = inject(TransportSegmentsApiService);
	private readonly datePipe = inject(DatePipe);
	private readonly destroyRef = inject(DestroyRef);
	private readonly infobox = inject(InfoboxService);
	private readonly router = inject(Router);
	private readonly route = inject(ActivatedRoute);
	private readonly dateAdapter = inject(DateAdapter);

	ngOnInit(): void {
		this.dateAdapter.setLocale('pl-PL');
		const segmentIdParam = this.route.snapshot.paramMap.get('id');
		if (!segmentIdParam) {
			return;
		}
		const segmentId = Number(segmentIdParam);
		if (Number.isNaN(segmentId)) {
			return;
		}
		this.editedSegmentId.set(segmentId);
		this.loadSegmentForEdit(segmentId);
	}

	searchTerms(): void {
		if (this.filtersForm.invalid) {
			this.filtersForm.markAllAsTouched();
			return;
		}

		const formValue = this.filtersForm.getRawValue();
		const filters = {
			search: formValue.search.trim(),
			accommodation_after: this.formatDate(formValue.accommodationAfter),
			accommodation_before: this.formatDate(formValue.accommodationBefore),
		};

		this.transportSegmentsApiService
			.getTransportTerms(filters)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				this.lastSearchResults.set(result.results);
				this.syncSelectedEntitiesFromResults(result.results);
				this.transportTerms.set(this.buildTransportTermsForDisplay(result.results));
			});
	}

	isTermSelected(termId: number | undefined): boolean {
		if (termId === undefined || termId === null) {
			return false;
		}
		return this.selectedTerms().includes(termId);
	}

	toggleTermSelection(term: TransportTermList, checked: boolean): void {
		const termId = term.id;
		if (termId === undefined || termId === null) {
			return;
		}
		if (checked) {
			this.selectedTermEntities.update((current) => ({
				...current,
				[termId]: term,
			}));
		} else {
			this.selectedTermEntities.update((current) => {
				const next = { ...current };
				delete next[termId];
				return next;
			});
		}
		this.selectedTerms.update((current) => {
			if (checked) {
				if (current.includes(termId)) {
					return current;
				}
				return [...current, termId];
			}
			return current.filter((id) => id !== termId);
		});
	}

	isAllSelected(): boolean {
		const terms = this.transportTerms();
		if (!terms.length) {
			return false;
		}
		const selected = this.selectedTerms();
		return terms.every((term) => term.id !== undefined && term.id !== null && selected.includes(term.id));
	}

	toggleAllSelection(checked: boolean): void {
		const ids = this.transportTerms()
			.map((term) => term.id)
			.filter((id): id is number => id !== undefined && id !== null);
		if (checked) {
			const entitiesToAdd: Record<number, TransportTermList> = {};
			this.transportTerms().forEach((term) => {
				if (term.id !== undefined && term.id !== null) {
					entitiesToAdd[term.id] = term;
				}
			});
			this.selectedTermEntities.update((current) => ({
				...current,
				...entitiesToAdd,
			}));
			this.selectedTerms.update((current) => {
				const next = new Set(current);
				ids.forEach((id) => next.add(id));
				return Array.from(next);
			});
			return;
		}
		this.selectedTermEntities.update((current) => {
			const next = { ...current };
			ids.forEach((id) => {
				delete next[id];
			});
			return next;
		});
		this.selectedTerms.update((current) => current.filter((id) => !ids.includes(id)));
	}

	onSortChange({ active, direction }: Sort): void {
		if (!direction) {
			return;
		}
		const sortedTerms = [...this.transportTerms()].sort((a, b) => {
			const sortMultiplier = direction === 'asc' ? 1 : -1;
			switch (active) {
				case 'id':
					return ((a.id ?? 0) - (b.id ?? 0)) * sortMultiplier;
				case 'name':
					return (a.name ?? '').localeCompare(b.name ?? '') * sortMultiplier;
				case 'accommodationLower':
					return (a.accommodation?.lower ?? '').localeCompare(b.accommodation?.lower ?? '') * sortMultiplier;
				case 'accommodationUpper':
					return (a.accommodation?.upper ?? '').localeCompare(b.accommodation?.upper ?? '') * sortMultiplier;
				default:
					return 0;
			}
		});
		this.transportTerms.set(sortedTerms);
	}

	saveSegment(): void {
		if (this.nameControl.invalid || this.isSaving()) {
			this.nameControl.markAsTouched();
			return;
		}

		const payload = {
			name: this.nameControl.getRawValue().trim(),
			transportTerms: this.selectedTerms(),
		};

		this.isSaving.set(true);
		const segmentId = this.editedSegmentId();
		const saveRequest$ = segmentId
			? this.transportSegmentsApiService.updateSegment(segmentId, payload)
			: this.transportSegmentsApiService.createSegment(payload);

		saveRequest$
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => {
					this.isSaving.set(false);
				}),
			)
			.subscribe((createdSegment) => {
				this.infobox.success(`Pomyślnie ${segmentId ? 'zaktualizowano' : 'dodano'} segment`);
				if (segmentId) {
					return;
				}
				this.router.navigate(['/transport-segments', createdSegment.id]);
			});
	}

	private formatDate(date: Date | null): string | undefined {
		if (!date) {
			return undefined;
		}
		return this.datePipe.transform(date, 'yyyy-MM-dd') ?? undefined;
	}

	private syncSelectedEntitiesFromResults(terms: TransportTermList[]): void {
		const selectedSet = new Set(this.selectedTerms());
		if (!selectedSet.size) {
			return;
		}
		const selectedUpdates: Record<number, TransportTermList> = {};
		terms.forEach((term) => {
			if (term.id !== undefined && term.id !== null && selectedSet.has(term.id)) {
				selectedUpdates[term.id] = term;
			}
		});
		if (Object.keys(selectedUpdates).length) {
			this.selectedTermEntities.update((current) => ({
				...current,
				...selectedUpdates,
			}));
		}
	}

	private buildTransportTermsForDisplay(searchResults: TransportTermList[]): TransportTermList[] {
		const selectedIds = this.selectedTerms();
		const selectedEntities = this.selectedTermEntities();
		const selectedRows = selectedIds
			.map((id) => selectedEntities[id])
			.filter((term): term is TransportTermList => !!term);
		const selectedSet = new Set(selectedIds);
		const restRows = searchResults.filter((term) => !selectedSet.has(term.id ?? -1));
		return [...selectedRows, ...restRows];
	}

	private loadSegmentForEdit(segmentId: number): void {
		this.transportSegmentsApiService
			.getSegment(segmentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((segment) => {
				this.nameControl.setValue(segment.name ?? '');
			});

		this.transportSegmentsApiService
			.getTransportTermsBySegment(segmentId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				const terms = result.results;
				const selectedIds = terms
					.map((term) => term.id)
					.filter((id): id is number => id !== undefined && id !== null);
				const selectedEntities = terms.reduce<Record<number, TransportTermList>>((current, term) => {
					if (term.id !== undefined && term.id !== null) {
						current[term.id] = term;
					}
					return current;
				}, {});
				this.selectedTerms.set(selectedIds);
				this.selectedTermEntities.set(selectedEntities);
				this.lastSearchResults.set(terms);
				this.transportTerms.set(terms);
			});
	}
}
